import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { finalize, tap } from 'rxjs';

import { ContextBlocksService } from '../../../../core/context/context-blocks.service';
import {
  ClientContextBlock,
  ClientContextEntry,
  ContextSubjectKind,
} from '../../../../core/context/context-blocks.types';
import { CONTEXT_DIALOG } from '../../../../shared/context-blocks-panel/context-dialog';
import { IconComponent } from '../../../../shared/icon/icon';
import { ClientBlockDialogComponent, ClientBlockDialogData } from './client-block-dialog';

/**
 * The shared context blocks as the business and its agent see them (DOC-89 §4,
 * `UI-DA-89-05`; FLOW-391; DOC-89-A §3, FLOW-401).
 *
 * *Information supporting your application. CapX files what you send us here.
 * If it's easier, you can add to it yourself.* **One compact line per block**:
 * its name, and the date of the latest entry or *Nothing here yet*. Pressing
 * the line opens the block in a pop-up (`ClientBlockDialogComponent`) with its
 * entries and *Add to this*; the page never grows.
 *
 * 🚨 **Nothing here asks for anything, and nothing says something is wanted**:
 * no tick, no count, no *complete*, no colour, never *outstanding* / *missing*
 * / *please provide* (DOC-59; DOC-73 `UI-DA-73-03`). A block the business has
 * added to reads the same as one it has not. Ops's internal block is not here —
 * the server never sends it. An addition is recorded as the business's (or its
 * agent's, for the business) and is never changed or removed.
 */
@Component({
  selector: 'app-context-blocks-client',
  imports: [DatePipe, IconComponent],
  templateUrl: './context-blocks-client.html',
  styleUrl: './context-blocks-client.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContextBlocksClientComponent {
  readonly subjectKind = input.required<ContextSubjectKind>();
  readonly subjectId = input.required<string>();

  private readonly service = inject(ContextBlocksService);
  private readonly dialog = inject(MatDialog);

  protected readonly blocks = signal<ClientContextBlock[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);

  constructor() {
    effect(() => {
      const kind = this.subjectKind();
      const id = this.subjectId();
      if (kind && id) this.load(kind, id);
    });
  }

  private load(kind: ContextSubjectKind, id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .clientList(kind, id)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => this.blocks.set(res.blocks),
        error: () => this.error.set('This could not be loaded right now.'),
      });
  }

  /** The block in a pop-up; the page keeps its one line. The dialog reads the
   *  block through a signal, so an addition's re-read shows in it at once. */
  protected openBlock(block: ClientContextBlock): void {
    const live = computed(() => this.blocks().find((b) => b.short_id === block.short_id));
    const data: ClientBlockDialogData = {
      block: live,
      save: (draft) =>
        this.service
          .clientAddEntry(this.subjectKind(), this.subjectId(), block.short_id, {
            text: draft.text,
            file: draft.file,
          })
          .pipe(tap(() => this.load(this.subjectKind(), this.subjectId()))),
      openFile: (entry) => this.openFile(block, entry),
    };
    this.dialog.open(ClientBlockDialogComponent, { ...CONTEXT_DIALOG, data });
  }

  /** The document as filed, in a new tab. */
  private openFile(block: ClientContextBlock, entry: ClientContextEntry): void {
    this.service
      .clientEntryFile(this.subjectKind(), this.subjectId(), block.short_id, entry.short_id)
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener');
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        error: () => this.error.set('The document could not be opened.'),
      });
  }
}
